import { FC, useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import { Checkbox, FormControlLabel, CheckboxProps } from "@mui/material";
import { useGetListQuery } from "../../features/api/Api";
import { params, isExists, nestedHandler } from "../../utils";
import { styled } from "@mui/styles";

export interface RenderTree {
  id: string;
  name: string;
  children?: RenderTree[];
}

interface ICheckBoxTree {
  multiSelect: boolean;
  onChange: Function;
  name: string;
  value: any;
  recordName?: string;
}

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: 20,
  width: 20,
  height: 20,
  border: "2px solid #CCC",
  backgroundColor: "transparent",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,
    padding: "2px",
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23CCC'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "transparent",
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background: "rgba(206,217,224,.5)",
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: "#0063cc",
  border: "2px solid transparent",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,

    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "#0063cc",
  },
});

function BpCheckbox(props: CheckboxProps) {
  return (
    <Checkbox
      sx={{
        "&:hover": { bgcolor: "transparent" },
      }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ "aria-label": "Checkbox demo" }}
      {...props}
    />
  );
}

const CheckBoxTree: FC<ICheckBoxTree> = ({
  multiSelect,
  onChange,
  name,
  value,
  recordName,
}) => {
  const [selected, setSelected] = useState<string[]>(
    multiSelect ? value : value === "" ? [] : [value]
  );
  const [categoryData, setCategoryData] = useState<any[]>([]);

  const { data: categories, isLoading } = useGetListQuery<any>({
    resource: "catalog/category",
    params: { ...params, pagination: { size: 50, pageNumber: 0 } },
  });

  useEffect(() => {
    if (isExists(categories?.data)) {
      setCategoryData(nestedHandler(categories.data, null));
    }
  }, [isLoading, categories]);

  function getChildById(node: any, id: string) {
    let array: string[] = [];

    function getAllChild(nodes: RenderTree | null) {
      if (nodes === null) return [];
      array.push(nodes.id);
      if (Array.isArray(nodes.children)) {
        nodes.children.forEach((node) => {
          //array = [...array, ...getAllChild(node)];
          array = array.filter((v, i) => array.indexOf(v) === i);
        });
      }

      return array;
    }

    function getNodeById(nodes: RenderTree, id: string) {
      if (nodes.id === id) {
        return nodes;
      } else if (Array.isArray(nodes.children)) {
        let result = null;
        nodes.children.forEach((node) => {
          if (!!getNodeById(node, id)) {
            result = getNodeById(node, id);
          }
        });
        return result;
      }

      return null;
    }

    return getAllChild(getNodeById(node, id));
  }

  function getOnChange(checked: boolean, nodes: RenderTree) {
    const allNode: string[] = getChildById(nodes, nodes.id);
    let array = checked
      ? [...selected, ...allNode]
      : selected.filter((value) => !allNode.includes(value));

    array = array.filter((v, i) => array.indexOf(v) === i);

    if (multiSelect) {
      if (recordName) {
        setSelected(array);
        let customArray: any = [];
        array.map((item) => customArray.push({ [recordName]: item }));
        onChange(name, customArray);
      } else {
        setSelected(array);
        onChange(name, array);
      }
    } else {
      if (array[0] === "00000000-0000-0000-0000-000000000000") {
        if (array.length <= 2) {
          setSelected(array);
          onChange(name, array[1]);
        }
      } else {
        if (array.length <= 1) {
          setSelected(array);
          onChange(name, array[0]);
        }
      }
    }
  }

  const renderTree = (nodes: RenderTree) => (
    <TreeItem
      key={nodes.id}
      nodeId={nodes.id}
      label={
        <FormControlLabel
          control={
            <BpCheckbox
              checked={selected.some((item) => item === nodes.id)}
              onChange={(event) =>
                getOnChange(event.currentTarget.checked, nodes)
              }
              onClick={(e) => e.stopPropagation()}
            />
          }
          label={<>{nodes.name}</>}
          key={nodes.id}
        />
      }
    >
      {Array.isArray(nodes.children)
        ? nodes.children.map((node) => renderTree(node))
        : null}
    </TreeItem>
  );

  return (
    <>
      {categoryData.length > 0 && (
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpanded={categories.data.map((category: any) => category.id)}
          defaultExpandIcon={<ChevronRightIcon />}
          multiSelect={false}
        >
          {categoryData?.length > 0 &&
            categoryData.map((category) => renderTree(category))}
        </TreeView>
      )}
    </>
  );
};

export default CheckBoxTree;
